import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search, ArrowRight, Calendar, Sparkles, Filter, X, ChevronRight, Star,
  Building2, BookOpen, Mic
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';
import BrandLogo from '../components/BrandLogo';
import { CaseStudyCard, normalizeCaseStudy, PartnerLogoGrid } from '../components/case-studies/CaseStudyCard';
import { CALENDLY_URL } from '../data/workshops';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudiesIndex() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ industries: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState(searchParams.get('industry') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');

  // SEO meta
  useEffect(() => {
    document.title = 'Case Studies & Past Clients | The WE Mentality';
    const meta = document.querySelector('meta[name="description"]') || document.createElement('meta');
    meta.setAttribute('name', 'description');
    meta.setAttribute('content', 'Real engagements with school districts, federal agencies, universities, chambers, and government partners. Leadership development, workforce training, and culture alignment outcomes by The WE Mentality.');
    if (!meta.parentNode) document.head.appendChild(meta);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, facetRes] = await Promise.all([
        axios.get(`${API}/case-studies`, { params: { limit: 200 } }),
        axios.get(`${API}/case-studies/facets`),
      ]);
      setItems(listRes.data?.items || []);
      setFacets(facetRes.data || { industries: [], categories: [] });
    } catch (err) {
      console.error('[CaseStudies] Load failed:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sync URL params
  useEffect(() => {
    const params = {};
    if (industry) params.industry = industry;
    if (category) params.category = category;
    setSearchParams(params, { replace: true });
  }, [industry, category, setSearchParams]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (industry && it.industry !== industry) return false;
      if (category && it.workshop_category !== category) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${it.org_name} ${it.short_name} ${it.engagement_title} ${it.description} ${it.outcome}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, industry, category, search]);

  const featured = useMemo(() => items.filter((i) => i.featured).slice(0, 3), [items]);

  const clearFilters = () => { setIndustry(''); setCategory(''); setSearch(''); };
  const hasFilters = !!(industry || category || search);

  const partnersForGrid = useMemo(() => items.map((p) => ({
    name: p.short_name || p.org_name,
    sub: p.org_name,
    logo: p.logo_url || null,
    website: p.website_url || null,
  })), [items]);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* ─────────────────────────── HEADER ─────────────────────────── */}
      <header className="absolute top-0 left-0 right-0 z-30 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="cs-logo-link">
            <BrandLogo className="h-10 sm:h-12 w-auto" fallbackClassName="text-base sm:text-lg font-bold tracking-wide text-white whitespace-nowrap" testId="cs-header-logo-img" />
          </a>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/workshops')} variant="ghost" className="hidden sm:inline-flex text-slate-300 hover:text-white hover:bg-white/5" data-testid="cs-nav-workshops">
              Workshops
            </Button>
            <Button onClick={() => navigate('/assessment')} variant="outline" className="hidden sm:inline-flex border-violet-500/50 text-violet-300 hover:bg-violet-500/10" data-testid="cs-nav-assessment">
              Free Assessment
            </Button>
            <Button onClick={() => window.open(CALENDLY_URL, '_blank')} className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-semibold" data-testid="cs-nav-book">
              Book a Strategy Session
            </Button>
          </div>
        </div>
      </header>

      {/* ─────────────────────────── HERO ─────────────────────────── */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[700px] h-[700px] bg-violet-500/15 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/3 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/15 to-cyan-500/15 border border-violet-500/30 mb-6">
            <Sparkles className="h-4 w-4 text-violet-400" />
            <span className="text-violet-300 text-sm font-semibold">Case Studies & Past Clients</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6">
            Real Engagements.{' '}
            <span className="bg-gradient-to-r from-violet-400 via-cyan-400 to-violet-400 bg-clip-text text-transparent">Honest Outcomes.</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed mb-8">
            Explore how The WE Mentality has partnered with school districts, federal agencies, universities,
            chambers, and government organizations to develop leaders and align teams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => window.open(CALENDLY_URL, '_blank')} size="lg" className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25" data-testid="cs-hero-book-btn">
              <Calendar className="mr-2 h-5 w-5" /> Book a Strategy Session
            </Button>
            <Button onClick={() => navigate('/assessment')} size="lg" variant="outline" className="border-slate-700 text-white hover:bg-white/5 px-8 py-6 text-lg rounded-xl" data-testid="cs-hero-assessment-btn">
              Take the WE Alignment Index™
            </Button>
          </div>
        </div>
      </section>

      {/* ─────────────────────────── FEATURED ─────────────────────────── */}
      {featured.length > 0 && (
        <section className="relative py-16 px-4 sm:px-6 lg:px-8 border-y border-white/5 bg-gradient-to-b from-white/[0.02] to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Star className="h-5 w-5 text-violet-400 fill-violet-400" />
              <h2 className="text-2xl sm:text-3xl font-bold">Featured Engagements</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cs-featured-grid">
              {featured.map((cs) => (
                <Link
                  key={cs.id}
                  to={`/case-studies/${cs.slug}`}
                  className="block focus:outline-none focus:ring-2 focus:ring-violet-500/50 rounded-2xl"
                  data-testid={`cs-featured-link-${cs.slug}`}
                >
                  <CaseStudyCard cs={normalizeCaseStudy(cs)} />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────── FILTERS + GRID ─────────────────────────── */}
      <section className="relative py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-cyan-400 font-semibold text-sm uppercase tracking-wider mb-2">All Case Studies</p>
              <h2 className="text-3xl sm:text-4xl font-bold">Browse by Industry or Category</h2>
            </div>
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search organizations, engagements, outcomes…"
                className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-11"
                data-testid="cs-search-input"
              />
            </div>
          </div>

          {/* Filter Chips */}
          <div className="space-y-4 mb-10">
            <FilterRow
              label="Industry"
              values={facets.industries}
              active={industry}
              onChange={setIndustry}
              testIdPrefix="cs-filter-industry"
            />
            <FilterRow
              label="Workshop Category"
              values={facets.categories}
              active={category}
              onChange={setCategory}
              testIdPrefix="cs-filter-category"
            />
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                data-testid="cs-clear-filters"
              >
                <X className="h-3.5 w-3.5" /> Clear all filters
              </button>
            )}
          </div>

          {/* Results */}
          <div className="text-sm text-slate-400 mb-6" data-testid="cs-results-count">
            {loading ? 'Loading…' : `Showing ${filtered.length} of ${items.length} case ${items.length === 1 ? 'study' : 'studies'}`}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 rounded-2xl border border-dashed border-white/10 bg-white/[0.02]">
              <Filter className="h-8 w-8 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No case studies match your filters.</p>
              <button onClick={clearFilters} className="mt-4 text-violet-400 hover:text-violet-300 text-sm font-semibold">Clear filters →</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cs-all-grid">
              {filtered.map((cs) => (
                <Link
                  key={cs.id}
                  to={`/case-studies/${cs.slug}`}
                  className="block focus:outline-none focus:ring-2 focus:ring-violet-500/50 rounded-2xl"
                  data-testid={`cs-card-link-${cs.slug}`}
                >
                  <CaseStudyCard cs={normalizeCaseStudy(cs)} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ─────────────────────────── PARTNER LOGO STRIP ─────────────────────────── */}
      {partnersForGrid.length > 0 && (
        <section className="relative py-16 px-4 sm:px-6 lg:px-8 border-t border-white/5 bg-gradient-to-b from-transparent via-violet-950/10 to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">Trusted By</p>
              <h2 className="text-3xl sm:text-4xl font-bold">Organizations We've Worked With</h2>
            </div>
            <PartnerLogoGrid items={partnersForGrid} testId="cs-partner-grid" />
          </div>
        </section>
      )}

      {/* ─────────────────────────── CTA BAND ─────────────────────────── */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto rounded-3xl border border-violet-500/30 bg-gradient-to-br from-violet-500/15 via-cyan-500/10 to-violet-500/15 p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-violet-500/20 rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-cyan-500/20 rounded-full blur-[100px]" />
          <div className="relative z-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Ready to write your <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">case study?</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Let's design a leadership, workforce, or culture engagement that fits your organization — and produces results worth telling.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => window.open(CALENDLY_URL, '_blank')} size="lg" className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-8 py-6 text-base rounded-xl" data-testid="cs-cta-book">
                <Calendar className="mr-2 h-5 w-5" /> Book a Strategy Session
              </Button>
              <Button onClick={() => navigate('/workshops')} size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-base rounded-xl" data-testid="cs-cta-workshops">
                <BookOpen className="mr-2 h-5 w-5" /> View Workshops
              </Button>
              <Button onClick={() => navigate('/assessment')} size="lg" variant="ghost" className="text-violet-300 hover:bg-violet-500/10 px-8 py-6 text-base rounded-xl" data-testid="cs-cta-assessment">
                Take WE Alignment Index™
              </Button>
            </div>
            <p className="mt-8 text-xs text-slate-400 flex items-center gap-2 justify-center">
              <Mic className="h-3.5 w-3.5" />
              Also available for conferences, retreats, workforce programs, and leadership events.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-9 w-auto" fallbackClassName="text-sm font-bold tracking-wide text-white whitespace-nowrap" testId="cs-footer-logo-img" />
            <span className="text-slate-400 text-sm">© {new Date().getFullYear()} {companyInfo.name}</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <a href="/" className="text-slate-400 hover:text-white">Home</a>
            <a href="/workshops" className="text-slate-400 hover:text-white">Workshops</a>
            <a href="/case-studies" className="text-slate-400 hover:text-white">Case Studies</a>
            <a href="/assessment" className="text-slate-400 hover:text-white">Assessment</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FilterRow({ label, values, active, onChange, testIdPrefix }) {
  if (!values || values.length === 0) return null;
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-semibold">{label}</p>
      <div className="flex flex-wrap gap-2">
        <FilterChip label="All" active={!active} onClick={() => onChange('')} testId={`${testIdPrefix}-all`} />
        {values.map((v) => (
          <FilterChip key={v} label={v} active={active === v} onClick={() => onChange(active === v ? '' : v)} testId={`${testIdPrefix}-${v.replace(/\W+/g, '-').toLowerCase()}`} />
        ))}
      </div>
    </div>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
        active
          ? 'bg-gradient-to-r from-violet-600 to-cyan-500 border-transparent text-white shadow-lg shadow-violet-500/20'
          : 'bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06] hover:border-violet-500/30'
      }`}
    >
      {label}
    </button>
  );
}
